import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Mail, MessageSquare, Clock, RotateCcw, Check } from "lucide-react";

import { submitContactMessage } from "@/lib/contact.functions";
import { useFormDraft } from "@/hooks/use-form-draft";

const emptyForm = { name: "", email: "", phone: "", subject: "", message: "" };
const DRAFT_KEY = "adapted:contact-draft";
const MESSAGE_MAX = 2000;

function formatSavedAt(stamp: number) {
  return new Date(stamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function ContactFormSection() {
  const submit = useServerFn(submitContactMessage);
  const [startedAt] = useState(() => Date.now());
  const [honeypot, setHoneypot] = useState("");
  const {
    values: form,
    setValues: setForm,
    restored,
    savedAt,
    clearDraft,
  } = useFormDraft(DRAFT_KEY, emptyForm);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [draftNoticeDismissed, setDraftNoticeDismissed] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const canSubmit = useMemo(
    () =>
      form.name.trim().length > 1 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
      form.message.trim().length > 4,
    [form.name, form.email, form.message]
  );

  // Warn before leaving with unsent input (draft is kept, but the nudge helps).
  useEffect(() => {
    const dirty = Object.values(form).some((v) => v.trim() !== "");
    if (!dirty || status === "success") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [form, status]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    setErrorMessage("");
    try {
      await submit({ data: { ...form, website: honeypot, startedAt } });
      setStatus("success");
      clearDraft();
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Something went wrong. Please try again."
      );
    }
  };

  const inputClass =
    "w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20";
  const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground";

  const showDraftNotice = restored && !draftNoticeDismissed && status !== "success";

  return (
    <section id="contact" className="relative bg-background py-24">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:items-start">
        <div>
          <h2 className="font-display text-4xl text-foreground sm:text-5xl">Get in touch</h2>
          <p className="mt-4 max-w-md text-sm text-muted-foreground">
            Questions about adaptED, partnerships or the pilot programme? Send us a message and our
            team will get back to you.
          </p>

          <ul className="mt-10 space-y-5">
            {[
              { icon: MessageSquare, title: "Send a message", body: "Use the form on this page — we read every message." },
              { icon: Clock, title: "Response time", body: "We usually reply within two working days." },
              { icon: Mail, title: "Email us", body: "contact@adapted.info", href: "mailto:contact@adapted.info" },
            ].map(({ icon: Icon, title, body, href }) => (
              <li key={title} className="flex gap-4">
                <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary-strong">
                  <Icon className="size-5" />
                </span>
                <div>
                  <p className="font-display text-sm font-bold text-foreground">{title}</p>
                  {href ? (
                    <a href={href} className="mt-1 block text-sm text-primary-strong underline-offset-4 hover:underline">
                      {body}
                    </a>
                  ) : (
                    <p className="mt-1 text-sm text-muted-foreground">{body}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {status === "success" ? (
          <div className="rounded-2xl bg-card p-8 text-center shadow-[var(--shadow-card)]">
            <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-primary/20">
              <Check className="size-7 text-primary-strong" />
            </div>
            <h3 className="mt-5 font-display text-2xl text-foreground">Message sent</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Thank you for reaching out. We&apos;ll be in touch soon.
            </p>
            <button
              type="button"
              onClick={() => setStatus("idle")}
              className="mt-6 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
            >
              Send another message
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl bg-card p-6 shadow-[var(--shadow-card)] sm:p-8"
          >
            {showDraftNotice && (
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3">
                <p className="text-xs text-foreground">
                  We restored your unsent draft
                  {savedAt ? ` from ${formatSavedAt(savedAt)}` : ""}.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      clearDraft();
                      setDraftNoticeDismissed(true);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                  >
                    <RotateCcw className="size-3.5" /> Start fresh
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraftNoticeDismissed(true)}
                    className="rounded-full px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Keep it
                  </button>
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className={labelClass}>
                  Full name
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                  maxLength={100}
                  autoComplete="name"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className={labelClass}>
                  Email address
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  maxLength={255}
                  autoComplete="email"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-phone" className={labelClass}>
                  Phone <span className="normal-case text-muted-foreground">(optional)</span>
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="e.g. 082 000 0000"
                  maxLength={50}
                  autoComplete="tel"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-subject" className={labelClass}>
                  Subject <span className="normal-case text-muted-foreground">(optional)</span>
                </label>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="What is this about?"
                  maxLength={150}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="contact-message" className={labelClass}>
                Message
              </label>
              <textarea
                id="contact-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="How can we help?"
                required
                rows={6}
                maxLength={MESSAGE_MAX}
                className={`resize-none ${inputClass}`}
              />
              <div className="mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
                <span aria-live="polite">
                  {savedAt ? `Draft saved at ${formatSavedAt(savedAt)}` : "Your draft saves automatically"}
                </span>
                <span>
                  {form.message.length}/{MESSAGE_MAX}
                </span>
              </div>
            </div>

            {/* Honeypot: hidden from real users, catches bots that fill every field */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="pointer-events-none absolute left-[-9999px] size-0 opacity-0"
            />

            {status === "error" && (
              <p role="alert" className="mt-4 text-sm text-destructive">
                {errorMessage}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "submitting" || !canSubmit}
              className="mt-6 w-full rounded-full bg-primary py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "submitting" ? "Sending..." : "Send message"}
            </button>

            <p className="mt-4 text-xs text-muted-foreground">
              We respect your privacy and will only use your details to respond to your enquiry.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
